import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Hidden, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PromocodeImportAttributes } from '@loyalty/shared-common/rewards-api-client';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { useAlerts } from '@qualifio/ui-commons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../../shared/layout/Loader';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';
import { useRewards } from '../../RewardsContext';

const REFRESH_INTERVAL = 5000; // milliseconds

export function UploadPromocodes({
  rewardId,
  onUploaded,
  onUpdate,
  onFinish,
}: {
  rewardId: string;
  onUploaded: (reward: PromocodeImportAttributes) => void;
  onUpdate?: (promocodeImport: PromocodeImportAttributes) => void;
  onFinish?: () => void;
}) {
  const { rewardsApi } = useApi();
  const { t } = useTranslation();
  const settingsQuery = useQuery({
    queryKey: ['rewards-settings'],
    queryFn: () => rewardsApi.getSettings(),
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { notify } = useAlerts();
  const { features } = useFeatures();
  const { write: promocodeWrite } = features[Feature.PROMOCODE];

  const [loading, setLoading] = useState(false);
  const [intervalRef, setIntervalRef] = useState<number | undefined>();

  const { rewards, selectedReward } = useRewards();

  if (!selectedReward) {
    notify('No selected reward', 'error');
    throw new Error('No selected reward');
  }

  useEffect(
    () => () => {
      clearInterval(intervalRef);
    },
    [intervalRef],
  ); // clear interval on dismount

  const refresh = useCallback(
    (id: string) => {
      return rewardsApi
        .getPromocodeImport(id)
        .then(promocodeImport => {
          onUpdate?.(promocodeImport);
          return promocodeImport.done || promocodeImport.error ? 'stop' : 'keepRefreshing';
        })
        .catch(error => {
          console.error(`Could not refresh promocode import ${id}`, error);
          return 'stop' as const;
        });
    },
    [onUpdate],
  );

  const keepRefreshing = (promocodeImportId: string) => {
    setIntervalRef(
      setInterval(async () => {
        if ((await refresh(promocodeImportId)) === 'stop') {
          stopRefreshing();
          onFinish?.();
        }
      }, REFRESH_INTERVAL) as unknown as number,
    );
  };

  const stopRefreshing = () => {
    clearInterval(intervalRef);
    setIntervalRef(undefined);
  };

  const handleUpload = async ({ target }: ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];

    if (!file) {
      return;
    }

    if (settingsQuery.data && file.size >= settingsQuery.data.MAX_FILE_SIZE) {
      notify(
        t('rewards.promocodes.upload.error.fileSize', {
          maxSize: `${Math.round(settingsQuery.data.MAX_FILE_SIZE / 1024 / 1024)}Mb`,
        }),
        'error',
      );
      return;
    }

    setLoading(true);
    try {
      const promocodeImport = await rewardsApi.uploadPromocodes(rewardId, file);
      const status = await refresh(promocodeImport.id);
      if (status === 'keepRefreshing') {
        keepRefreshing(promocodeImport.id);
      }
      onUploaded(promocodeImport);
    } catch (e) {
      notify('Could not upload file', 'error');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      setLoading(false);
    }
  }, [rewards]);

  return settingsQuery.isPending ? (
    <Loader />
  ) : (
    <>
      <Hidden xsUp xsDown implementation="css">
        <input type="file" name="file" accept=".csv" ref={fileInputRef} onChange={handleUpload} />
      </Hidden>
      <Tooltip
        title={
          !promocodeWrite
            ? t('shared.action.permission.required')
            : selectedReward.deletedAt
            ? t('rewards.archived.upload-promocodes.forbidden')
            : ''
        }
        placement="right"
      >
        <div>
          <Button
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => fileInputRef?.current?.click()}
            disabled={loading || !promocodeWrite || !!selectedReward.deletedAt}
            color="primary"
            id={'import-promocode-button-id'}
          >
            {t('rewards.promo-codes.button.add-promo-codes')}
            {loading && <CircularProgress size="15px" color="secondary" sx={{ marginLeft: 1.5 }} />}
          </Button>
        </div>
      </Tooltip>
    </>
  );
}
